import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Camera, CheckCircle2, AlertTriangle, ScanLine, Package, MapPin, Warehouse } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import useOfflineSync from '../hooks/useOfflineSync';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * WarehouseTaskScanner — camera-based Code 39 / Code 128 / QR scanner
 * for Picking and Put-Away tasks.
 *
 * Picking task → user must scan the pallet sticker (`kind = pallet`).
 * Put-away task → two-step: scan the pallet sticker (`kind = pallet`)
 *                 then the location barcode (`kind = location`). The
 *                 item code was captured upstream at Receiving against
 *                 the Pallet ID, so Put-Away no longer prompts for the
 *                 item barcode (Oct 2026 user request).
 *
 * Behaviour on mismatch: shows a red banner + toast, keeps the scanner
 * running so the user can immediately retry a different pallet/item.
 * A per-code cooldown (2s) stops the same misfire spamming the API.
 */
const WarehouseTaskScanner = ({ open, task, authHeaders, onClose, onCompleted, onVerified }) => {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const lastScanRef = useRef({ code: '', at: 0 });
  // Feb 2026 — Ref that always points to the latest `handleDecoded`.
  // The ZXing continuous-decode callback is registered ONCE per open,
  // so it closes over the initial render's `handleDecoded` (and by
  // extension its `nextKind`). Without this indirection every scan
  // after the first sent `kind: 'item'` even after the item step
  // completed. Refs bypass React's closure trap.
  const decodedHandlerRef = useRef(null);
  const [status, setStatus] = useState('starting');
  const [errMsg, setErrMsg] = useState('');
  const [banner, setBanner] = useState(null); // {kind:'success'|'error', message:string}
  const [verified, setVerified] = useState({ pallet: false, item: false, location: false });
  const [completing, setCompleting] = useState(false);
  const [manualEntry, setManualEntry] = useState('');
  const { toast } = useToast();
  const { submit: submitOffline } = useOfflineSync();

  const isPicking = task?.type === 'picking';
  const isPutaway = task?.type === 'putaway';

  // Determine which scan step is currently expected.
  // Oct 2026 — Put-away first step is now `pallet` (not `item`). The
  // item code was captured at Receiving against the Pallet ID so the
  // floor operator only has to pair pallet → location now.
  const nextKind = (() => {
    if (isPicking) return 'pallet';
    if (isPutaway) return verified.pallet ? 'location' : 'pallet';
    return 'pallet';
  })();

  const allDone = isPicking ? verified.pallet : (verified.pallet && verified.location);

  // Feb 2026 — Clear stale banner/toast state the moment the active
  // step changes (e.g. item just got verified → location step). This
  // stops the previous step's error message from lingering under the
  // yellow pill and confusing the floor user about which field the
  // app is now waiting on.
  useEffect(() => {
    setBanner(null);
    setManualEntry('');
    lastScanRef.current = { code: '', at: 0 };
  }, [nextKind]);

  useEffect(() => {
    if (!open) return () => {};
    setStatus('starting');
    setErrMsg('');
    setBanner(null);
    setVerified({ pallet: false, item: false, location: false });
    setManualEntry('');
    // Feb 2026 — Explicit reset so switching tasks doesn't inherit
    // the previous task's cooldown-guarded barcode. Without this a
    // task that legitimately shares a pallet ID (e.g. two putaway
    // tasks off the same pallet) would silently swallow the second
    // scan.
    lastScanRef.current = { code: '', at: 0 };
    let cancelled = false;

    (async () => {
      try {
        // Dynamic import so ZXing (~200 KB gzip) is only fetched when
        // the scanner actually opens — keeps the initial paint tight
        // on low-end Android devices where the floor page is used.
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        const { BarcodeFormat, DecodeHintType } = await import('@zxing/library');
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.CODE_39,
          BarcodeFormat.CODE_128,
          BarcodeFormat.QR_CODE,
        ]);
        const reader = new BrowserMultiFormatReader(hints);
        readerRef.current = reader;
        const controls = await reader.decodeFromConstraints(
          // 720p video is enough for Code 39 decoding and much lighter
          // on low-end phones than the default 1080p.
          { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } },
          videoRef.current,
          (result) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              // Always dispatch through the ref so `nextKind` /
              // `verified` state changes take effect immediately
              // instead of being frozen at effect-setup time.
              decodedHandlerRef.current?.(text);
            }
          },
        );
        controlsRef.current = controls;
        if (!cancelled) setStatus('scanning');
      } catch (err) {
        if (cancelled) return;
        const name = err?.name || '';
        let msg = err?.message || 'Could not access camera.';
        if (name === 'NotAllowedError') {
          msg = 'Camera permission denied. Tap the camera icon in your browser address bar to allow it.';
        } else if (name === 'NotFoundError') {
          msg = 'No camera was found on this device.';
        } else if (name === 'NotReadableError') {
          msg = 'Your camera is being used by another app. Close it and try again.';
        }
        setErrMsg(msg);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch { /* noop */ }
      controlsRef.current = null;
      readerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  const verify = async (code) => {
    const clean = String(code || '').trim().toUpperCase();
    if (!clean) return;
    // Feb 2026 — Route through the offline-sync queue. Online: normal
    // POST + validate. Offline (or transient network drop): enqueue,
    // optimistically advance state so the picker can keep working —
    // the drain loop will replay when the network returns and mark
    // any server-side mismatch as a conflict on the badge.
    try {
      const res = await submitOffline({
        url: `${API}/admin/warehouse/tasks/${task.id}/verify-scan`,
        method: 'post',
        body: { scanned: clean, kind: nextKind },
        headers: authHeaders,
        kind: `verify-scan:${task.id}:${nextKind}`,
      });
      if (res.queued) {
        setBanner({
          kind: 'success',
          message: `Saved offline — ${nextKind} will re-verify when the device is back online.`,
        });
        setVerified((prev) => ({ ...prev, [nextKind]: true }));
        try { navigator.vibrate?.(60); } catch { /* noop */ }
        if (typeof onVerified === 'function') onVerified(nextKind);
        return;
      }
      const data = res.data || {};
      if (data.ok) {
        setBanner({ kind: 'success', message: data.message || `${nextKind} verified.` });
        setVerified((prev) => ({ ...prev, [nextKind]: true }));
        try { navigator.vibrate?.(60); } catch { /* noop */ }
        if (typeof onVerified === 'function') onVerified(nextKind);
      } else {
        setBanner({ kind: 'error', message: data.message || 'Mismatch — try again.' });
        try { navigator.vibrate?.([200, 80, 200]); } catch { /* noop */ }
        toast({ title: `Scan mismatch (${nextKind})`, description: data.message || 'Try another barcode.' });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Verify failed';
      setBanner({ kind: 'error', message: detail });
      toast({ title: 'Verify failed', description: detail });
    }
  };

  const handleDecoded = (text) => {
    const clean = String(text || '').trim().toUpperCase();
    if (!clean) return;
    const now = Date.now();
    // Same-code cooldown: 2s to stop ZXing spamming us with the same
    // frame on continuous decode.
    if (lastScanRef.current.code === clean && (now - lastScanRef.current.at) < 2000) return;
    lastScanRef.current = { code: clean, at: now };
    verify(clean);
  };

  // Feb 2026 — Keep the ref pointed at the LATEST `handleDecoded` so
  // the ZXing continuous-decode callback (registered once on open)
  // always dispatches through the freshest closure — with the
  // freshest `nextKind` / `verified` state. Without this, the
  // location scan after item verification incorrectly sent
  // `kind: 'item'` because the callback was frozen at first render.
  useEffect(() => {
    decodedHandlerRef.current = handleDecoded;
  });

  const complete = async () => {
    if (!allDone) return;
    setCompleting(true);
    try {
      const res = await submitOffline({
        url: `${API}/admin/warehouse/tasks/${task.id}`,
        method: 'patch',
        body: { status: 'completed' },
        headers: authHeaders,
        kind: `complete-task:${task.id}`,
      });
      if (res.queued) {
        toast({
          title: 'Saved offline',
          description: `${task.type} completion queued — will sync automatically.`,
        });
      } else {
        toast({ title: 'Task completed', description: `${task.type} task marked done.` });
        if (typeof onCompleted === 'function') onCompleted(res.data);
      }
      onClose();
    } catch (e) {
      toast({ title: 'Failed to complete', description: e?.response?.data?.detail || e?.message });
    } finally {
      setCompleting(false);
    }
  };

  if (!open) return null;

  const stepBadge = (label, k, icon) => {
    const done = verified[k];
    const active = k === nextKind && !done;
    const IconEl = icon;
    return (
      <div
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
          done ? 'bg-emerald-500/20 text-emerald-100' :
          active ? 'bg-amber-500/25 text-amber-100 ring-2 ring-amber-400 animate-pulse' :
          'bg-white/10 text-white/60'
        }`}
        data-testid={`scan-step-${k}`}
      >
        <IconEl className="w-3.5 h-3.5" />
        {label}
        {done && <CheckCircle2 className="w-3.5 h-3.5" />}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center px-3 py-6 overflow-y-auto"
      data-testid="warehouse-task-scanner"
    >
      <div className="relative w-full max-w-md bg-[#0F172A] rounded-3xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 pt-4 pb-2 text-white">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="font-display text-base leading-tight">
                Scan {task?.type === 'picking' ? 'pallet' : 'pallet + location'}
              </div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">
                {task?.type} · {task?.product_name}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            data-testid="warehouse-task-scanner-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="px-4 pb-2 flex gap-2 flex-wrap">
          {isPicking && stepBadge('Pallet', 'pallet', Warehouse)}
          {isPutaway && stepBadge('Pallet', 'pallet', Warehouse)}
          {isPutaway && stepBadge('Location', 'location', MapPin)}
        </div>

        {banner && (
          <div
            className={`mx-4 mb-2 px-3 py-2 rounded-lg text-sm font-medium flex items-start gap-2 ${
              banner.kind === 'success'
                ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/40'
                : 'bg-red-500/20 text-red-100 border border-red-400/40'
            }`}
            data-testid={`scan-banner-${banner.kind}`}
          >
            {banner.kind === 'success'
              ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <div>{banner.message}</div>
          </div>
        )}

        <div className="relative aspect-[4/3] bg-black">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />
          {status === 'starting' && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Starting camera…
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white">
              <Camera className="w-8 h-8 text-red-400 mb-3" />
              <div className="text-sm leading-relaxed">{errMsg}</div>
            </div>
          )}
          {status === 'scanning' && !allDone && (
            <>
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-[8%] right-[8%] top-1/2 -translate-y-1/2 aspect-[5/2] border-2 border-emerald-400 rounded-2xl">
                  <div className="absolute -top-px left-0 right-0 h-1 bg-emerald-400/70 animate-pulse rounded" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3 text-center text-white text-xs bg-gradient-to-t from-black/80 to-transparent">
                Point at the <span className="font-bold text-emerald-300">{nextKind}</span> barcode
              </div>
            </>
          )}
          {allDone && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white bg-emerald-900/70">
              <CheckCircle2 className="w-12 h-12 text-emerald-300 mb-3" />
              <div className="text-lg font-semibold">All scans verified</div>
              <div className="text-xs text-emerald-100 mt-1">Tap &quot;Complete task&quot; to finish.</div>
            </div>
          )}
        </div>

        <div className="p-4 space-y-3 text-white text-xs">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/60 mb-1">
              Or type barcode manually
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={manualEntry}
                onChange={(e) => setManualEntry(String(e.target.value || '').toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && manualEntry.trim()) {
                    verify(manualEntry.trim());
                    setManualEntry('');
                  }
                }}
                placeholder={nextKind === 'pallet' ? 'PLT-000001' : 'Location ID'}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                className="flex-1 bg-white/10 border border-white/20 rounded-md px-2 py-1.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-emerald-400 uppercase"
                style={{ textTransform: 'uppercase' }}
                data-testid="scan-manual-input"
              />
              <button
                type="button"
                disabled={!manualEntry.trim()}
                onClick={() => { verify(manualEntry.trim()); setManualEntry(''); }}
                className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                data-testid="scan-manual-verify"
              >
                Verify
              </button>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              onClick={onClose}
              variant="ghost"
              className="flex-1 text-white border border-white/20 hover:bg-white/10"
              data-testid="scan-close-btn"
            >
              Close
            </Button>
            <Button
              onClick={complete}
              disabled={!allDone || completing}
              className={`flex-1 ${allDone ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-white/10'} text-white font-bold`}
              data-testid="scan-complete-btn"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete task'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseTaskScanner;
